import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { useClientsQuery } from '@/hooks/useClientsQuery';
import { useCreatePractitionerConversationMutation } from '@/hooks/useChat';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { SEARCH_DEBOUNCE_MILLISECONDS, ADMIN_LIST_PAGE_SIZE } from '@/constants/adminPagination';
import { Icon } from '@/components/common/Icon';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { showErrorNotification } from '@/utils/notifications';

interface NewConversationDialogProps {
  onClose: () => void;
  onConversationStarted: (conversationId: string) => void;
}

export function NewConversationDialog({
  onClose,
  onConversationStarted,
}: NewConversationDialogProps) {
  const { t } = useTranslation('chat');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MILLISECONDS);
  const clientsQuery = useClientsQuery(page, debouncedSearch, 'active');
  const createConversationMutation = useCreatePractitionerConversationMutation();
  const clients = clientsQuery.data === undefined ? [] : clientsQuery.data.items;
  const totalItems = clientsQuery.data === undefined ? 0 : clientsQuery.data.totalItems;
  const hasSearch = debouncedSearch.trim().length > 0;

  useEffect(
    function resetPageOnSearchChange() {
      setPage(1);
    },
    [debouncedSearch]
  );

  async function startConversationWithClient(clientId: string) {
    try {
      const conversation = await createConversationMutation.mutateAsync(clientId);
      if (conversation.id !== null) {
        onConversationStarted(conversation.id);
      }
    } catch (creationError) {
      const backendMessage = resolveApiErrorMessage(creationError);
      showErrorNotification(
        backendMessage.length > 0 ? backendMessage : t('chat:errors.createConversationFailed')
      );
    }
  }

  return (
    <ModalOverlay onRequestClose={onClose}>
      <div className="w-full max-w-md bg-zoowhite rounded-2xl shadow-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-zooborder">
          <Input
            value={search}
            onChange={function updateSearch(changeEvent) {
              setSearch(changeEvent.target.value);
            }}
            placeholder={t('chat:list.searchPlaceholder')}
            autoFocus
          />
        </div>
        <div className="max-h-80 overflow-y-auto">
          {clientsQuery.isLoading === true ? (
            <div className="py-8 flex justify-center">
              <LoadingSpinner className="w-6 h-6" />
            </div>
          ) : clients.length === 0 ? (
            <p className="px-5 py-8 text-sm text-zoomuted text-center">
              {hasSearch === true
                ? t('chat:newConversation.noMatches')
                : t('chat:newConversation.noClients')}
            </p>
          ) : (
            <>
              {hasSearch === false && totalItems > ADMIN_LIST_PAGE_SIZE ? (
                <p className="px-5 pt-3 text-[11px] text-zoomuted">
                  {t('chat:newConversation.searchPrompt', { count: totalItems })}
                </p>
              ) : null}
              <ul className="divide-y divide-zooborder">
                {clients.map(function renderClient(client) {
                  const isCreatingAnyConversation = createConversationMutation.isPending;
                  const isStartingConversationWithClient =
                    isCreatingAnyConversation && createConversationMutation.variables === client.id;
                  return (
                    <li key={client.id}>
                      <button
                        type="button"
                        disabled={isCreatingAnyConversation}
                        aria-busy={isStartingConversationWithClient === true ? true : undefined}
                        onClick={function select() {
                          startConversationWithClient(client.id);
                        }}
                        className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-zoovpalelavender transition-colors disabled:opacity-50"
                      >
                        {isStartingConversationWithClient === true ? (
                          <LoadingSpinner className="w-4 h-4" />
                        ) : (
                          <Icon name="User" className="text-zooviolet" />
                        )}
                        <span className="text-sm font-semibold text-zooindigo truncate">
                          {client.fullName}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
        {totalItems > ADMIN_LIST_PAGE_SIZE ? (
          <div className="px-5 py-3 border-t border-zooborder">
            <ListPagination
              page={page}
              pageSize={ADMIN_LIST_PAGE_SIZE}
              totalItems={totalItems}
              onPageChange={setPage}
            />
          </div>
        ) : null}
      </div>
    </ModalOverlay>
  );
}
